import { invert, darken, desaturate, lighten } from 'polished';
import { pipeInto } from 'ts-functional-pipe';
export function getTheme({ rtl, colorTheme }: { rtl: boolean; colorTheme: 'light' | 'dark' }) {
  const theme = {
    rtl,
    colors: getColors(colorTheme),
    fontSizes: {
      small: '10px',
      normal: '12px',
      heading: '16px',
      medium: '14px',
      large: '20px',
      extraLarge: '28px',
    },
    borderRadius: {
      small: '6px',
      normal: '8px',
      medium: '10px',
      large: '16px',
      extraLarge: '24px',
    },
    buttonSize: {
      small: '8px 16px',
      medium: '10px 20px',
      large: '12px 24px',
    },
    shadow: {
      light: '0 4px 8px rgba(0, 0, 0, 0.1)',
      card: '0 1px 2px -1px hsl(224 40% 30% / 0.10), 0 2px 6px -1px hsl(224 40% 30% / 0.08)',
    },
  };
  return theme;
}

type PrototypeColors = {
  border: string;
  input: string;
  ring: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  destructive: string;
  destructiveForeground: string;
  destructiveText: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  accentText: string;
  success: string;
  successForeground: string;
  successText: string;
  warning: string;
  warningForeground: string;
  warningText: string;
  neutralBar: string;
  level1: string;
  level2: string;
  level3: string;
  level4: string;
  level5: string;
  level1Text: string;
  level2Text: string;
  level3Text: string;
  level4Text: string;
  level5Text: string;
  userPrimary: string;
  userPrimaryText: string;
  adminPrimary: string;
  adminPrimaryText: string;
  viewPrimary: string;
  coinBg: string;
  coinAccent: string;
  coinAccentText: string;
  brand: string;
  brandStrong: string;
  brandDeep: string;
  brandDeepest: string;
  sidebarBackground: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
};

type Colors = {
  pageBackground: string;
  bodyBackground: string;
  navbarBackground: string;
  panelBackground: string;
  insetPanelBackground: string;
  topBarBackground: string;
  topBarBorder: string;
  topBarShadow: string;
  topBarSubtleBackground: string;
  topBarHoverBackground: string;
  topBarAccent: string;
  normal: string;
  heading: string;
  strong: string;
  anchor: string;
  primary: string;
  primaryContrast: string;
  error: string;
  success: string;
  urgent: string;
  urgentBackground: string;
  waningText: string;
  warningBackground: string;
  tableBorder: string;
  userLevel: string;
  trainingCardBackground: string;
  trainingCardButton: string;
  managerPrimary: string;
  statusDanger: string;
  mutedForeground: string;
  textDark: string;
  // Prototype version
  prototype: PrototypeColors;
};
function getColors(colorTheme: 'light' | 'dark') {
  return colorTheme === 'dark'
    ? ({
        pageBackground: pipeInto('#EEF1F9', invert, darken(0.1)),
        bodyBackground: pipeInto('#f5f7f3', invert, darken(0.1)),
        panelBackground: '#252525',
        insetPanelBackground: pipeInto('#eef1f9', invert, darken(0.04)),
        topBarBackground: 'rgba(37, 37, 37, 0.92)',
        topBarBorder: pipeInto('#B6BEDC', invert, desaturate(0.3)),
        topBarShadow: '0 4px 14px rgba(0, 0, 0, 0.22)',
        topBarSubtleBackground: 'rgba(255, 255, 255, 0.06)',
        topBarHoverBackground: 'rgba(255, 255, 255, 0.1)',
        topBarAccent: '#A9C34D',
        normal: pipeInto('#596180', invert, desaturate(0.2), lighten(0.1)),
        heading: invert('#292B33'),
        strong: invert('#151515'),
        anchor: '#5170ef',
        primary: '#4c5aff',
        managerPrimary: '#FF6F5A',
        primaryContrast: '#FFFFFF',
        error: invert('#933547'),
        success: invert('#3ABF87'),
        urgent: pipeInto('#C11414', lighten(0.1)),
        urgentBackground: invert('#FFF1F1'),
        waningText: invert('#B1410C'),
        warningBackground: invert('#FFF9EB'),
        tableBorder: pipeInto('#B6BEDC', invert, desaturate(0.3)),
        userLevel: invert('#5601D6'),
        trainingCardBackground: invert('#F7F8FA'),
        trainingCardButton: invert('#DDE7FF'),
        statusDanger: invert('#E93354'),
        navbarBackground: '#2e2e2e',
        mutedForeground: pipeInto('#606b85', invert, desaturate(0.2), lighten(0.1)),
        textDark: invert('#1c202c'),
        // Prototype version
        prototype: {
          border: 'hsl(0 0% 18%)',
          input: 'hsl(0 0% 20%)',
          ring: 'hsl(224 61% 51%)',
          background: 'hsl(89 16% 7%)',
          card: 'hsl(0 0% 8%)',
          popover: 'hsl(0 0% 10%)',
          primary: 'hsl(224 61% 51%)',
          secondary: 'hsl(0 0% 14%)',
          muted: 'hsl(0 0% 14%)',
          accent: 'hsl(224 61% 51%)',
          accentText: 'hsl(224 66% 73%)',
          userPrimary: 'hsl(224 61% 51%)',
          userPrimaryText: 'hsl(224 66% 77%)',
          viewPrimary: 'hsl(224 61% 51%)',
          brand: 'hsl(89 17% 53%)',
          brandStrong: 'hsl(89 25% 38%)',
          brandDeep: 'hsl(89 29% 22%)',
          brandDeepest: 'hsl(89 31% 12%)',
          foreground: 'hsl(220 22% 96%)',
          cardForeground: 'hsl(220 22% 96%)',
          popoverForeground: 'hsl(220 22% 96%)',
          primaryForeground: 'hsl(0 0% 100%)',
          secondaryForeground: 'hsl(220 22% 96%)',
          destructive: 'hsl(348 75% 56%)',
          destructiveForeground: 'hsl(0 0% 100%)',
          destructiveText: 'hsl(352 90% 72%)',
          mutedForeground: 'hsl(220 14% 66%)',
          accentForeground: 'hsl(0 0% 100%)',
          success: 'hsl(157 50% 50%)',
          successForeground: 'hsl(160 60% 96%)',
          successText: 'hsl(157 55% 62%)',
          warning: 'hsl(35 88% 60%)',
          warningForeground: 'hsl(28 80% 12%)',
          warningText: 'hsl(38 92% 64%)',
          neutralBar: 'hsl(223 14% 34%)',
          level1: 'hsl(5 100% 68%)',
          level2: 'hsl(35 92% 60%)',
          level3: 'hsl(224 91% 64%)',
          level4: 'hsl(149 48% 52%)',
          level5: 'hsl(270 95% 66%)',
          level1Text: 'hsl(5 100% 74%)',
          level2Text: 'hsl(35 95% 64%)',
          level3Text: 'hsl(224 95% 74%)',
          level4Text: 'hsl(149 52% 62%)',
          level5Text: 'hsl(270 100% 80%)',
          adminPrimary: 'hsl(78 58% 58%)',
          adminPrimaryText: 'hsl(78 62% 62%)',
          coinBg: 'hsl(40 92% 62%)',
          coinAccent: 'hsl(37 93% 58%)',
          coinAccentText: 'hsl(40 95% 66%)',
          sidebarBackground: 'hsl(223 24% 11%)',
          sidebarForeground: 'hsl(220 14% 80%)',
          sidebarPrimary: 'hsl(224 91% 64%)',
          sidebarPrimaryForeground: 'hsl(0 0% 100%)',
          sidebarAccent: 'hsl(223 20% 18%)',
          sidebarAccentForeground: 'hsl(220 22% 96%)',
          sidebarBorder: 'hsl(223 16% 22%)',
          sidebarRing: 'hsl(224 91% 64%)',
        },
      } satisfies Colors)
    : ({
        pageBackground: '#EEF1F9',
        bodyBackground: '#f5f7f3',
        panelBackground: '#FFFFFF',
        insetPanelBackground: '#eef1f9',
        topBarBackground: 'rgba(255, 255, 255, 0.9)',
        topBarBorder: '#D5DAEE',
        topBarShadow: '0 4px 12px rgba(12, 23, 54, 0.04)',
        topBarSubtleBackground: '#FBFCFF',
        topBarHoverBackground: '#F1F5FD',
        topBarAccent: '#B9D15A',
        normal: '#596180',
        heading: '#292B33',
        strong: '#151515',
        anchor: '#5170ef',
        primary: '#365ece',
        managerPrimary: '#FF6F5A',
        primaryContrast: '#FFFFFF',
        error: '#933547',
        success: '#3ABF87',
        urgent: '#C11414',
        urgentBackground: '#FFF1F1',
        waningText: '#B1410C',
        warningBackground: '#FFF9EB',
        tableBorder: '#B6BEDC',
        userLevel: '#5601D6',
        trainingCardBackground: '#F7F8FA',
        trainingCardButton: '#DDE7FF',
        statusDanger: '#E93354',
        navbarBackground: '#FFFFFF',
        mutedForeground: '#606b85',
        textDark: '#1c202c',
        // Prototype version
        prototype: {
          border: 'hsl(220 16% 90%)',
          input: 'hsl(220 16% 90%)',
          ring: 'hsl(224 61% 51%)',
          background: 'hsl(89 20% 96%)',
          card: 'hsl(0 0% 100%)',
          popover: 'hsl(0 0% 100%)',
          primary: 'hsl(224 61% 51%)',
          secondary: 'hsl(220 14% 96%)',
          muted: 'hsl(220 14% 96%)',
          accent: 'hsl(224 61% 51%)',
          accentText: 'hsl(224 61% 45%)',
          userPrimary: 'hsl(224 61% 51%)',
          userPrimaryText: 'hsl(224 61% 49%)',
          viewPrimary: 'hsl(224 61% 51%)',
          brand: 'hsl(89 17% 53%)',
          brandStrong: 'hsl(89 25% 38%)',
          brandDeep: 'hsl(89 29% 22%)',
          brandDeepest: 'hsl(89 31% 12%)',
          foreground: 'hsl(224 22% 14%)',
          cardForeground: 'hsl(224 22% 14%)',
          popoverForeground: 'hsl(224 22% 14%)',
          primaryForeground: 'hsl(0 0% 100%)',
          secondaryForeground: 'hsl(224 22% 14%)',
          destructive: 'hsl(348 82% 56%)',
          destructiveForeground: 'hsl(0 0% 100%)',
          destructiveText: 'hsl(348 82% 48%)',
          mutedForeground: 'hsl(223 16% 45%)',
          accentForeground: 'hsl(0 0% 100%)',
          success: 'hsl(157 53% 49%)',
          successForeground: 'hsl(164 74% 9%)',
          successText: 'hsl(157 53% 32%)',
          warning: 'hsl(35 90% 60%)',
          warningForeground: 'hsl(28 80% 20%)',
          warningText: 'hsl(35 90% 33%)',
          neutralBar: 'hsl(224 24% 82%)',
          level1: 'hsl(5 100% 69%)',
          level2: 'hsl(35 90% 60%)',
          level3: 'hsl(224 91% 60%)',
          level4: 'hsl(149 45% 49%)',
          level5: 'hsl(270 100% 50%)',
          level1Text: 'hsl(5 100% 44%)',
          level2Text: 'hsl(35 90% 33%)',
          level3Text: 'hsl(224 91% 57%)',
          level4Text: 'hsl(149 45% 34%)',
          level5Text: 'hsl(270 100% 60%)',
          adminPrimary: 'hsl(78 55% 56%)',
          adminPrimaryText: 'hsl(78 55% 31%)',
          coinBg: 'hsl(37 100% 75%)',
          coinAccent: 'hsl(37 93% 58%)',
          coinAccentText: 'hsl(37 93% 32%)',
          sidebarBackground: 'hsl(0 0% 98%)',
          sidebarForeground: 'hsl(240 5.3% 26.1%)',
          sidebarPrimary: 'hsl(240 5.9% 10%)',
          sidebarPrimaryForeground: 'hsl(0 0% 98%)',
          sidebarAccent: 'hsl(240 4.8% 95.9%)',
          sidebarAccentForeground: 'hsl(240 5.9% 10%)',
          sidebarBorder: 'hsl(220 13% 91%)',
          sidebarRing: 'hsl(217.2 91.2% 59.8%)',
        },
      } satisfies Colors);
}
